import React, { useMemo } from "react";
import Donut from "./shared/Donut";
import { COLORS } from "../constants/analyticsConstants";
import {
  deriveKpis,
  transformEventAnalytics,
  transformScreenAnalytics,
} from "../utils/analyticsUtils";

/* ── Animated SVG Area Chart ─────────────────────────────────────────────── */
function AreaChart({ data, color = "#6366F1" }) {
  if (!data.length) return <p style={{ color:"var(--t3)", fontSize:13 }}>No trend data yet</p>;
  const W = 600, H = 110, PAD = 8;
  const values = data.map((d) => d.count ?? d.value ?? 0);
  const max = Math.max(...values, 1);
  const pts = values.map((v, i) => {
    const x = PAD + (i / Math.max(values.length - 1, 1)) * (W - PAD * 2);
    const y = H - PAD - ((v / max) * (H - PAD * 2));
    return [x, y];
  });
  const linePath = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length-1][0]},${H} L${pts[0][0]},${H} Z`;
  const gradId   = `ag-${color.replace("#","")}`;

  return (
    <div className="ua-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity="0.28"/>
            <stop offset="80%"  stopColor={color} stopOpacity="0.04"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="glass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,.18)"/>
            <stop offset="100%" stopColor="rgba(255,255,255,0)"/>
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f, i) => (
          <line key={i}
            x1={PAD} y1={H - PAD - f*(H-PAD*2)}
            x2={W-PAD} y2={H - PAD - f*(H-PAD*2)}
            stroke="rgba(99,102,241,.07)" strokeWidth="1" strokeDasharray="4 4"
          />
        ))}
        <path d={areaPath} fill={`url(#${gradId})`}/>
        <path d={areaPath} fill="url(#glass)"/>
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5"
          strokeLinecap="round" strokeLinejoin="round"
          className="ua-chart-path"
        />
        {pts.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i === pts.length-1 ? 5 : 3.5}
            fill={color} stroke="#fff" strokeWidth="2"
            style={{ opacity: 0, animation: `ua-in .3s ease ${.8 + i*.05}s forwards` }}
          />
        ))}
      </svg>
    </div>
  );
}

/* ── KPI Card ────────────────────────────────────────────────────────────── */
function KpiCard({ label, value, unit, delta, sub }) {
  const pos = delta == null || delta >= 0;
  return (
    <div className="ua-kpi-card">
      <div className="ua-kpi-label">{label}</div>
      <div className="ua-kpi-value">
        {value ?? "—"}
        {unit && <span className="ua-kpi-unit">{unit}</span>}
      </div>
      {sub && <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 4 }}>{sub}</div>}
      {delta != null && (
        <div className={`ua-kpi-delta ${pos ? "up" : "dn"}`}>
          {pos ? "↑" : "↓"} {Math.abs(delta)}%
          <span className="ua-kpi-hint">vs last period</span>
        </div>
      )}
    </div>
  );
}

/* ── H-Bar ───────────────────────────────────────────────────────────────── */
function HBar({ label, value, max, color, sub, delay = 0 }) {
  const pct = Math.round((value / Math.max(max, 1)) * 100);
  return (
    <div className="ua-hbar">
      <div className="ua-hbar-row">
        <span className="ua-hbar-label">{label}</span>
        <span className="ua-hbar-val">
          {value.toLocaleString()}
          {sub && <span style={{ opacity:.6 }}> · {sub}</span>}
        </span>
      </div>
      <div className="ua-hbar-track">
        <div className="ua-hbar-fill" style={{ width:`${pct}%`, background:color, animationDelay:`${delay}s` }} />
      </div>
    </div>
  );
}

/* ── Card wrapper ────────────────────────────────────────────────────────── */
function CardHead({ title, sub }) {
  return (
    <div className="ua-card-head">
      <span className="ua-card-title">{title}</span>
      {sub && <span className="ua-card-sub">{sub}</span>}
    </div>
  );
}

/* ── Main ────────────────────────────────────────────────────────────────── */
export default function OverviewTab({ data, range }) {
  const kpis       = useMemo(() => deriveKpis(data), [data]);
  const eventDist  = useMemo(() => transformEventAnalytics(data?.event_analytics), [data]);
  const screenDist = useMemo(() => transformScreenAnalytics(data?.screen_analytics), [data]);
  const maxEvent   = Math.max(...eventDist.map(e => e.count), 1);
  const maxScreen  = Math.max(...screenDist.map(s => s.views), 1);

  /* App opens count from event_analytics */
  const appOpenCount = useMemo(() => {
    const series = data?.event_analytics?.APP_OPENED ?? [];
    return series.reduce((s, d) => s + d.count, 0);
  }, [data]);

  /* Event volume trend */
  const eventTrend = useMemo(() => {
    const byDate = {};
    Object.values(data?.event_analytics ?? {}).forEach(series =>
      series.forEach(({ date, count }) => { byDate[date] = (byDate[date] ?? 0) + count; })
    );
    return Object.entries(byDate).sort(([a],[b]) => a.localeCompare(b)).map(([date, count]) => ({ date, count }));
  }, [data]);

  /* Platform donut */
  const platformDist = useMemo(() => {
    const counts = {};
    (data?.events?.items ?? []).forEach(e => {
      const p = e.device_info?.platform ?? "unknown";
      counts[p] = (counts[p] ?? 0) + 1;
    });
    const total = Object.values(counts).reduce((a,b) => a+b, 0) || 1;
    const pal = { android: COLORS.teal[3], ios: COLORS.purple[3], web: COLORS.amber[3], unknown: COLORS.gray[3] };
    return Object.entries(counts).map(([type, count]) => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      pct: Math.round((count/total)*100),
      color: pal[type.toLowerCase()] ?? COLORS.gray[3],
    }));
  }, [data]);

  const rangeLabel = { "7d": "last 7 days", "30d": "last 30 days", "90d": "last 90 days" }[range] || range;

  return (
    <div className="ua-vstack">

      {/* ── KPI Row: App Opens · Sessions · Avg Session · Unique Screens ── */}
      <div className="ua-kpi-grid">
        <KpiCard
          label="App Opened"
          value={appOpenCount}
          sub={rangeLabel}
        />
        <KpiCard
          label="Total Sessions"
          value={kpis?.totalSessions}
          sub={rangeLabel}
        />
        <KpiCard
          label="Avg Session Duration"
          value={kpis?.avgSessionMin ?? "—"}
          unit="min"
        />
        <KpiCard
          label="Unique Screens"
          value={kpis?.uniqueScreens}
        />
      </div>

      {/* ── Event volume + Platform ── */}
      <div className="ua-grid-2-1">
        <div className="ua-card b" style={{ animationDelay:".05s" }}>
          <CardHead title="Event volume" sub="aggregated by date" />
          <AreaChart data={eventTrend} color="#6366F1" />
          <div className="ua-chart-axis">
            <span>{eventTrend[0]?.date}</span>
            <span>{eventTrend[eventTrend.length-1]?.date}</span>
          </div>
        </div>
        <div className="ua-card t" style={{ animationDelay:".10s" }}>
          <CardHead title="Platform split" />
          {platformDist.length > 0
            ? <Donut data={platformDist} />
            : <p style={{ color:"var(--t3)", fontSize:13 }}>No platform data</p>
          }
        </div>
      </div>

      {/* ── Event distribution + Screen views ── */}
      <div className="ua-grid-1-1">
        <div className="ua-card" style={{ animationDelay:".15s" }}>
          <CardHead title="Event distribution" sub={`${eventDist.reduce((s,e)=>s+e.count,0)} total`} />
          {eventDist.map((e, i) => (
            <HBar key={e.name} label={e.displayName} value={e.count} max={maxEvent} color={e.color} delay={i*.06} />
          ))}
        </div>
        <div className="ua-card a" style={{ animationDelay:".20s" }}>
          <CardHead title="Screen views" sub="by screen name" />
          {screenDist.map((s, i) => (
            <HBar key={s.screen} label={s.screen} value={s.views} max={maxScreen}
              color={s.color} sub={`${s.sessions} session${s.sessions!==1?"s":""}`} delay={i*.06} />
          ))}
        </div>
      </div>

    </div>
  );
}